/* eslint-disable react/jsx-curly-newline */
/* eslint-disable @typescript-eslint/no-shadow */
import { useMemo } from 'react';
import * as React from 'react';

import Button from '@/components/elements/Button/BaseButton';
import CheckBox from '@/components/elements/Form/BetCheckBox';
import { BetType } from '@/lib/apollo/types/gen/graphql';
import {
  BetMethod,
  FirstThreeRacerData,
  RacerNumber,
  WheelAxis,
  WheelRacerData,
} from '@/modelTypes';

import {
  ActionButtons,
  ButtonWrapper,
  Table,
  TableData,
  TableHeader,
  TableWrapper,
} from '../style';

interface WheelSheetProps {
  betType: BetType;
  racerNumbers: RacerNumber[];
  wheelAxis: WheelAxis;
  firstThreeRacerList: WheelRacerData;
  setFirstThreeRacerData: React.Dispatch<
    React.SetStateAction<FirstThreeRacerData>
  >;
  isDisabledBicycleNumber: (bicycleNum: number) => boolean;
}

const WheelSheet: React.FC<WheelSheetProps> = ({
  betType,
  racerNumbers,
  wheelAxis,
  firstThreeRacerList,
  setFirstThreeRacerData,
  isDisabledBicycleNumber,
}) => {
  const isBracket = [BetType.BracketPerfecta, BetType.BracketQuinella].includes(
    betType,
  );

  /**
   * 各軸タイプにおける軸でない（相手）indexを取得する
   * ex.
   * 1着軸->1着（index0）以外は相手なので[1,2]
   * 1・3着軸->1・3着(index0,index2)以外は相手なので[1]
   */
  const getOpponentIndexList = (): number[] => {
    if (wheelAxis === WheelAxis.First || wheelAxis === WheelAxis.One) {
      return [1, 2];
    }
    if (wheelAxis === WheelAxis.Second) {
      return [0, 2];
    }
    if (wheelAxis === WheelAxis.Third) {
      return [0, 1];
    }
    if (wheelAxis === WheelAxis.FirstSecond || wheelAxis === WheelAxis.Two) {
      return [2];
    }
    if (wheelAxis === WheelAxis.FirstThird) {
      return [1];
    }
    if (wheelAxis === WheelAxis.SecondThird) {
      return [0];
    }
    return [];
  };

  const opponentIndex = (): number => {
    if (wheelAxis === WheelAxis.Two || wheelAxis === WheelAxis.FirstSecond) {
      return 2;
    }
    if (wheelAxis === WheelAxis.SecondThird) {
      return 0;
    }
    return 1;
  };

  /**
   * UI上での軸indexをFirstThreeRacerListに対応するindexに変換する
   * ex. 1着軸->0, 2着軸->1, 3着軸->2
   */
  const axisIndex = (index: number) => {
    if (wheelAxis === WheelAxis.Second) {
      if (index === 0) return 1;
      return 0;
    }
    if (wheelAxis === WheelAxis.Third) {
      if (index === 0) return 2;
      return 0;
    }
    return index;
  };

  /**
   * @param num 枠単・枠複の場合->枠番, それ以外の場合->車番
   * @param index {軸番号-1}の値
   */
  const isChecked = (num: number, index: number) => {
    return firstThreeRacerList[betType][wheelAxis][axisIndex(index)].some(
      racerNum => {
        if (isBracket) return racerNum.bracketNum === num;
        return racerNum.bicycleNum === num;
      },
    );
  };

  /**
   * @param num 枠単・枠複の場合->枠番, それ以外の場合->車番
   */
  const isContainNumber = (num: number) =>
    firstThreeRacerList[betType][wheelAxis].flat().some(racerNum => {
      if (isBracket) return racerNum.bracketNum === num;
      return racerNum.bicycleNum === num;
    });

  /**
   * @param bracketNum 枠番
   * @return boolean 同じ枠の選手が2人とも欠車 -> true
   */
  const isDisabledBracketNumber = (bracketNum: number) =>
    racerNumbers
      .filter(rn => rn.bracketNum === bracketNum)
      .every(rn => isDisabledBicycleNumber(rn.bicycleNum));

  /**
   * @param bracketNum 枠番
   * @return boolean 同じ枠の選手のうち片方が欠車であり、1着軸、2着軸、相手のいづれかがチェックされているとき -> true
   */
  const isDisabledOneSideBracketNumber = (
    bracketNum: number,
    index: number,
  ) => {
    // 同じ枠の選手のどちらかが欠車になっているか場合
    if (
      racerNumbers
        .filter(rn => rn.bracketNum === bracketNum)
        .some(rn => isDisabledBicycleNumber(rn.bicycleNum))
    ) {
      return isContainNumber(bracketNum) && !isChecked(bracketNum, index);
    }
    return false;
  };

  /**
   * @param num 枠単・枠複の場合->枠番, それ以外の場合->車番
   * @param index {軸番号-1}の値
   */
  const isDisabled = (num: number, index: number) => {
    if (isBracket && isDisabledBracketNumber(num)) return true;
    if (isBracket && isDisabledOneSideBracketNumber(num, index)) return true;
    if (!isBracket && isDisabledBicycleNumber(num)) return true;
    return !isBracket && isContainNumber(num) && !isChecked(num, index);
  };

  const getBracketNumber = (bicycleNum: number) => {
    const racer = racerNumbers.find(
      racerNum => racerNum.bicycleNum === bicycleNum,
    );
    return racer!.bracketNum;
  };

  const getRowSpan = (bicycleNum: number) =>
    racerNumbers.filter(rn => rn.bracketNum === getBracketNumber(bicycleNum))
      .length;

  const columnCount = useMemo(
    () =>
      isBracket
        ? Math.max(...racerNumbers.map(rn => rn.bracketNum))
        : racerNumbers.length,
    [betType, racerNumbers],
  );

  const rowCount = useMemo(
    () =>
      [
        WheelAxis.First,
        WheelAxis.Second,
        WheelAxis.Third,
        WheelAxis.One,
      ].includes(wheelAxis)
        ? 2
        : 3,
    [wheelAxis],
  );

  /**
   * 2枠単・2枠複の場合 -> 相手軸を全てチェック
   * そうでない場合 -> 軸に選択されていない車番を全て相手軸にチェック
   */
  const arrayCheck = () => {
    setFirstThreeRacerData(prev => {
      const newList = { ...prev };

      const checkedNumList = newList[BetMethod.Wheel][betType][wheelAxis]
        .filter((_, i) => !getOpponentIndexList().includes(i))
        .flat()
        .filter(rn =>
          isBracket
            ? !isDisabledBracketNumber(rn.bracketNum)
            : !isDisabledBicycleNumber(rn.bicycleNum),
        )
        .map(rn => {
          if (isBracket) return rn.bracketNum;
          return rn.bicycleNum;
        });
      getOpponentIndexList().forEach(index => {
        newList[BetMethod.Wheel][betType][wheelAxis][index] = isBracket
          ? [...racerNumbers].filter(
              rn =>
                !isDisabledBracketNumber(rn.bracketNum) &&
                !isDisabledOneSideBracketNumber(rn.bracketNum, index),
            )
          : racerNumbers
              .filter(rn => !isDisabledBicycleNumber(rn.bicycleNum))
              .filter(rn => {
                if (isBracket) return !checkedNumList.includes(rn.bracketNum);
                return !checkedNumList.includes(rn.bicycleNum);
              });
      });
      return newList;
    });
  };

  /**
   * 相手軸のチェックを全て外す
   */
  const unsetArrayCheck = () => {
    setFirstThreeRacerData(prev => {
      const newList = { ...prev };
      getOpponentIndexList().forEach(index => {
        newList[BetMethod.Wheel][betType][wheelAxis][index] = [];
      });
      return newList;
    });
  };

  const setData = (
    racerNums: RacerNumber[],
    index: number,
    isChecked: boolean,
  ) => {
    setFirstThreeRacerData(prev => {
      const newList = { ...prev };
      if (isChecked) {
        newList[BetMethod.Wheel][betType][wheelAxis][index].push(...racerNums);
      } else {
        newList[BetMethod.Wheel][betType][wheelAxis][index] = newList[
          BetMethod.Wheel
        ][betType][wheelAxis][index].filter(rn => {
          if (isBracket) {
            return !racerNums.map(rn => rn.bracketNum).includes(rn.bracketNum);
          }
          return !racerNums.map(rn => rn.bicycleNum).includes(rn.bicycleNum);
        });
      }
      return newList;
    });
  };

  const handleClick = (value: string, checked: boolean) => {
    const strArray = value.split(':');
    const num = Number(strArray[0]); // 車番or枠番
    const index = axisIndex(Number(strArray[1]));
    const racerNumArray = racerNumbers.filter(rn => {
      if (isBracket) return rn.bracketNum === num;
      return rn.bicycleNum === num;
    });

    if (getOpponentIndexList().includes(index)) {
      getOpponentIndexList().forEach(i => {
        setData(racerNumArray, i, checked);
      });
    } else {
      setData(racerNumArray, index, checked);
    }
  };

  return (
    <TableWrapper>
      <Table>
        <thead>
          <tr>
            {wheelAxis === WheelAxis.First && (
              <>
                <TableHeader>1着軸</TableHeader>
                <TableHeader>相手</TableHeader>
              </>
            )}
            {wheelAxis === WheelAxis.Second && (
              <>
                <TableHeader>2着軸</TableHeader>
                <TableHeader>相手</TableHeader>
              </>
            )}
            {wheelAxis === WheelAxis.Third && (
              <>
                <TableHeader>3着軸</TableHeader>
                <TableHeader>相手</TableHeader>
              </>
            )}
            {wheelAxis === WheelAxis.One && (
              <>
                <TableHeader>軸</TableHeader>
                <TableHeader>相手</TableHeader>
              </>
            )}
            {wheelAxis === WheelAxis.FirstSecond && (
              <>
                <TableHeader>1着軸</TableHeader>
                <TableHeader>2着軸</TableHeader>
                <TableHeader>相手</TableHeader>
              </>
            )}
            {wheelAxis === WheelAxis.FirstThird && (
              <>
                <TableHeader>1着軸</TableHeader>
                <TableHeader>相手</TableHeader>
                <TableHeader>3着軸</TableHeader>
              </>
            )}
            {wheelAxis === WheelAxis.SecondThird && (
              <>
                <TableHeader>相手</TableHeader>
                <TableHeader>2着軸</TableHeader>
                <TableHeader>3着軸</TableHeader>
              </>
            )}
            {wheelAxis === WheelAxis.Two && (
              <>
                <TableHeader>1軸</TableHeader>
                <TableHeader>2軸</TableHeader>
                <TableHeader>相手</TableHeader>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {[...Array(columnCount)].map((_, i) => (
            <tr key={`tr-${i}`}>
              {[...Array(rowCount)].map((_, j) => (
                <TableData
                  key={`td-${j}`}
                  span={isBracket ? getRowSpan(i + 1) : 1}
                  disabled={
                    isBracket
                      ? isDisabledBracketNumber(i + 1)
                      : isDisabledBicycleNumber(i + 1)
                  }
                >
                  <CheckBox
                    checked={isChecked(i + 1, j)}
                    disabled={isDisabled(i + 1, j)}
                    num={i + 1}
                    onChange={e =>
                      handleClick(`${i + 1}:${j}`, e.target.checked)
                    }
                  />
                </TableData>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
      <ButtonWrapper>
        {[...Array(rowCount)].map((_, i) => (
          <ActionButtons key={i}>
            {i === opponentIndex() && (
              <>
                <Button
                  bgColor="secondary"
                  size="s"
                  radius="s"
                  onClick={() => arrayCheck()}
                >
                  全
                </Button>
                <Button
                  bgColor="secondary"
                  size="s"
                  outline
                  radius="s"
                  onClick={() => unsetArrayCheck()}
                >
                  消
                </Button>
              </>
            )}
          </ActionButtons>
        ))}
      </ButtonWrapper>
    </TableWrapper>
  );
};

export default WheelSheet;
